import { Component, OnInit, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { combineLatest, finalize, startWith } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Currency } from '../../../models/exchange-rate/exchange-rate';
import { MonthlyUtilityCostResponse, ServiceCostRowState } from '../../../models/monthly-uploads/service-cost';
import { ServiceCostProxyService } from '../../../proxies/service-cost.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-service-costs-tab',
  templateUrl: './service-costs-tab.component.html',
  styleUrl: './service-costs-tab.component.scss',
})
export class ServiceCostsTabComponent implements OnInit {
  constructor(
    private readonly proxy: ServiceCostProxyService,
    private readonly translateService: TranslateService,
    private readonly enumService: EnumService,
  ) {}

  month = new FormControl<number | null>(null, [Validators.required]);
  year = new FormControl<number | null>(null, [Validators.required]);

  rows: ServiceCostRowState[] = [];
  currencyOptions: { label: string; value: number }[] = [];
  isLoadingPeriod = signal(false);

  ngOnInit(): void {
    this.rows = this.buildRows();
    this.currencyOptions = this.getCurrencyOptions();

    combineLatest([this.month.valueChanges.pipe(startWith(null)), this.year.valueChanges.pipe(startWith(null))])
      .pipe(filter(([month, year]) => month !== null && year !== null))
      .subscribe(([month, year]) => this.loadByPeriod(month!, year!));
  }

  private resetRows(): void {
    this.rows.forEach((r) => {
      r.value.reset();
      r.invoiceNumber.reset();
      r.currency.setValue(Currency.USD);
      r.file.set(null);
      r.hasExistingData.set(false);
      r.savedStatus.set('idle');
      r.errorMessage.set('');
    });
  }

  private loadByPeriod(month: number, year: number): void {
    this.resetRows();
    this.isLoadingPeriod.set(true);
    this.proxy
      .getByPeriod(year, month)
      .pipe(finalize(() => this.isLoadingPeriod.set(false)))
      .subscribe({
        next: (result) => {
          if (result.success && result.data) {
            this.populateRows(result.data);
          }
        },
      });
  }

  private populateRows(data: MonthlyUtilityCostResponse): void {
    data.utilities.forEach((utility) => {
      const row = this.rows.find((r) => r.serviceType === utility.serviceType);
      if (!row) return;
      row.value.setValue(utility.totalCost);
      row.currency.setValue(utility.currency);
      if (utility.invoiceNumber) {
        row.invoiceNumber.setValue(utility.invoiceNumber);
      }
      row.hasExistingData.set(true);
    });
  }

  monthOptions: { label: string; value: number }[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  yearOptions: { label: string; value: number }[] = (() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((v) => ({
      label: String(v),
      value: v,
    }));
  })();

  private buildRows(): ServiceCostRowState[] {
    const enumValues = this.enumService.getEnum('UtilityServiceType').values;
    const entries = Object.entries(enumValues);
    const types: { serviceType: number; label: string }[] =
      entries.length > 0
        ? entries.map(([key, label]) => ({ serviceType: Number(key), label: String(label) }))
        : [
            { serviceType: 0, label: 'Water' },
            { serviceType: 1, label: 'Energy' },
            { serviceType: 2, label: 'Steam' },
          ];

    return types.map(({ serviceType, label }) => ({
      label,
      serviceType,
      invoiceNumber: new FormControl<string | null>(null, [Validators.required]),
      currency: new FormControl<number>(Currency.USD, { nonNullable: true }),
      value: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
      file: signal<File | null>(null),
      hasExistingData: signal(false),
      isSaving: signal(false),
      savedStatus: signal<'idle' | 'success' | 'error'>('idle'),
      errorMessage: signal(''),
    }));
  }

  private getCurrencyOptions(): { label: string; value: number }[] {
    const enumValues = this.enumService.getEnum('Currency').values;
    const mapped = Object.entries(enumValues).map(([key, label]) => ({
      label,
      value: Number(key),
    }));

    if (mapped.length > 0) {
      return mapped;
    }

    return [
      { label: 'USD', value: Currency.USD },
      { label: 'HNL', value: Currency.HNL },
    ];
  }

  canSaveRow(row: ServiceCostRowState): boolean {
    return (
      this.month.value !== null &&
      this.year.value !== null &&
      row.invoiceNumber.valid &&
      row.value.valid &&
      !row.isSaving() &&
      !row.hasExistingData()
    );
  }

  saveRow(row: ServiceCostRowState): void {
    if (!this.canSaveRow(row)) return;

    row.savedStatus.set('idle');
    row.errorMessage.set('');
    row.isSaving.set(true);

    this.proxy
      .save(
        {
          month: this.month.value!,
          year: this.year.value!,
          serviceType: row.serviceType,
          invoiceNumber: row.invoiceNumber.value!,
          currency: row.currency.value,
          value: row.value.value!,
        },
        row.file() ?? undefined,
      )
      .pipe(finalize(() => row.isSaving.set(false)))
      .subscribe({
        next: (result) => {
          if (result.success) {
            row.savedStatus.set('success');
            GlobalMessageUtil.success(
              this.translateService.instant('Service_Costs_Save_Success'),
              `${row.label} ${this.translateService.instant('Service_Costs_Save_Success')}`,
            );
          } else {
            row.savedStatus.set('error');
            row.errorMessage.set(
              getLocalizedValue(this.translateService, result.errorMessage) || this.translateService.instant('Service_Costs_Save_Error'),
            );
          }
        },
        error: (error) => {
          row.savedStatus.set('error');
          row.errorMessage.set(
            getLocalizedValue(this.translateService, error?.error?.errorMessage) ||
              this.translateService.instant('Service_Costs_Save_Error'),
          );
        },
      });
  }

  onFileChange(row: ServiceCostRowState, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    row.file.set(input.files[0]);
    input.value = '';
  }

  clearFile(row: ServiceCostRowState): void {
    row.file.set(null);
  }
}
